import React from 'react';

import classNames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import voca from 'voca';

import StatusIndicator from 'components/application/indicators/StatusIndicator';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useProjectPersonnelContext } from 'components/projectPersonnel/contexts/ProjectPersonnelContext';

export default function ProjectPersonnelTrainingStatusDetails({ status }) {
  const {
    division: {
      attributes: { singularRAMSName },
    },
  } = useCurrentActor();

  const { projectStartDate } = useProjectPersonnelContext();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix:
      'projectPersonnel.components.ProjectPersonnelTrainingStatusDetails',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const inFuture = projectStartDate && moment(projectStartDate) > moment();

  const trainingStatusOptions = {
    0: {
      status: 'high',
      label: i18nT('missingTraining'),
      labelStyling: 'tw-text-red-500',
    },
    1: {
      status: 'high',
      label: i18nTCommon('shared.expiryDateIsMissing'),
      labelStyling: 'tw-text-red-500',
    },
    2: {
      status: 'high',
      label:
        inFuture ?
          i18nT('expiresBefore', {
            interpolation: { escapeValue: false },
            singularRAMSName,
          })
        : i18nTCommon('shared.expired'),
      labelStyling: 'tw-text-red-500',
    },
    3: {
      status: 'medium',
      label: i18nT('expiresDuring', {
        interpolation: { escapeValue: false },
        singularRAMSName,
      }),
      labelStyling: 'tw-text-amber-600',
    },
    4: {
      status: 'low',
      label: i18nT('valid', {
        interpolation: { escapeValue: false },
        singularRAMSName,
      }),
      labelStyling: 'tw-text-green-400',
    },
    5: {
      status: 'grey',
      label: i18nTCommon('training.noRequiredTraining'),
      labelStyling: 'tw-text-grey-300',
    },
    6: {
      status: 'grey',
      label: i18nTCommon('projects.ended', {
        interpolation: { escapeValue: false },
        singularRAMSName: voca.capitalize(singularRAMSName),
      }),
      labelStyling: 'tw-text-grey-300',
    },
    7: {
      status: 'grey',
      label: i18nT('datesNotSet', {
        interpolation: { escapeValue: false },
        singularRAMSName: voca.capitalize(singularRAMSName),
      }),
      labelStyling: 'tw-text-grey-300',
    },
  }[status];

  return (
    <div className='tw-flex tw-h-8 tw-items-center tw-gap-2'>
      <StatusIndicator status={trainingStatusOptions.status} />
      <span
        className={classNames(
          'tw-text-s tw-font-medium tw-tracking-wide',
          trainingStatusOptions.labelStyling,
        )}
      >
        {trainingStatusOptions.label}
      </span>
    </div>
  );
}

ProjectPersonnelTrainingStatusDetails.propTypes = {
  status: PropTypes.number.isRequired,
};
